import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";

import {
  HiOutlineBuildingOffice2,
  HiOutlineEye,
  HiOutlineMagnifyingGlass,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineHashtag,
  HiOutlineCalendarDays,
  HiOutlineDocumentText,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";
import ConfirmModal from "../../../components/ui/confirmModal/ConfirmModal";

import usePermissions from "../../../hooks/usePermissions";

import {
  deleteOrganization,
  getOrganizations,
  setOrganizationFilters,
  setOrganizationPage,
} from "../../../redux/features/organizations/organizationSlice";

import "./OrganizationsPage.css";

export default function OrganizationsPage() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { can } = usePermissions();

  const { organizations, filters, pagination, isLoading, isDeleting, error } =
    useSelector((state) => state.organizations);

  const [searchValue, setSearchValue] = useState(filters.search || "");

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    organization: null,
  });

  /**
   * =====================================================
   * SEARCH
   * =====================================================
   */

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        setOrganizationFilters({
          search: searchValue,
        }),
      );
    }, 400);

    return () => clearTimeout(timer);
  }, [dispatch, searchValue]);

  /**
   * =====================================================
   * LOAD
   * =====================================================
   */

  useEffect(() => {
    dispatch(
      getOrganizations({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      }),
    );
  }, [dispatch, pagination.page, pagination.limit, filters]);

  /**
   * =====================================================
   * FILTER CHANGE
   * =====================================================
   */

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    if (name === "search") {
      setSearchValue(value);

      return;
    }

    dispatch(
      setOrganizationFilters({
        [name]: value,
      }),
    );
  };

  /**
   * =====================================================
   * DELETE
   * =====================================================
   */

  const handleDelete = async () => {
    if (!confirmModal.organization) return;

    try {
      await dispatch(
        deleteOrganization(confirmModal.organization._id),
      ).unwrap();

      setConfirmModal({
        isOpen: false,
        organization: null,
      });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * =====================================================
   * LOADING
   * =====================================================
   */

  if (isLoading && organizations.length === 0) {
    return <PageLoader title="Organisaties laden..." />;
  }

  return (
    <div className="organizations-page">
      {/* HEADER */}
      <PageHeader
        eyebrow="POWER"
        title="Organisaties"
        description="Beheer organisaties binnen het POWER platform."
        actions={
          can("organization.admin") && (
            <button
              className="organizations-page__create-button"
              onClick={() => navigate("/dashboard/organizations/new")}
            >
              <HiOutlinePlus />

              <span>Nieuwe organisatie</span>
            </button>
          )
        }
      />

      {/* ERROR */}
      {error && <div className="organizations-page__error">{error}</div>}

      {/* FILTERS */}
      <PageCard className="organizations-page__filters">
        <div className="organizations-page__filters-grid">
          {/* SEARCH */}
          <div className="organizations-page__search">
            <HiOutlineMagnifyingGlass />

            <input
              name="search"
              value={searchValue}
              onChange={handleFilterChange}
              placeholder="Zoek op naam of code..."
              autoComplete="off"
            />
          </div>

          {/* STATUS */}
          <select
            name="is_active"
            value={filters.is_active || ""}
            onChange={handleFilterChange}
          >
            <option value="">Alle statussen</option>

            <option value="true">Actief</option>

            <option value="false">Inactief</option>
          </select>
        </div>
      </PageCard>

      {/* EMPTY */}
      {organizations.length === 0 ? (
        <PageEmpty
          icon={<HiOutlineBuildingOffice2 />}
          title="Geen organisaties gevonden"
          description="Er zijn momenteel geen organisaties beschikbaar."
        />
      ) : (
        <>
          {/* GRID */}
          <div className="organizations-page__grid">
            {organizations.map((organization) => (
              <PageCard
                key={organization._id}
                className="organizations-page__card"
              >
                {/* TOP */}
                <div className="organizations-page__card-top">
                  <div>
                    <h3>{organization.name}</h3>

                    <p>
                      {organization.description ||
                        "Geen beschrijving beschikbaar."}
                    </p>
                  </div>

                  <span
                    className={`organizations-page__status ${
                      organization.is_active
                        ? "organizations-page__status--active"
                        : "organizations-page__status--inactive"
                    }`}
                  >
                    {organization.is_active ? "Actief" : "Inactief"}
                  </span>
                </div>

                {/* META */}
                <div className="organizations-page__meta">
                  <div>
                    <strong>
                      <HiOutlineHashtag />

                      <span>Code</span>
                    </strong>

                    <span>{organization.code}</span>
                  </div>

                  <div>
                    <strong>
                      <HiOutlineCalendarDays />

                      <span>Aangemaakt</span>
                    </strong>

                    <span>
                      {organization.createdAt
                        ? new Date(organization.createdAt).toLocaleDateString(
                            "nl-BE",
                          )
                        : "-"}
                    </span>
                  </div>

                  <div>
                    <strong>
                      <HiOutlineDocumentText />

                      <span>Status</span>
                    </strong>

                    <span>
                      {organization.is_active
                        ? "Actieve organisatie"
                        : "Inactieve organisatie"}
                    </span>
                  </div>
                </div>

                {/* ACTIONS */}
                <div className="organizations-page__actions">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/dashboard/organizations/${organization._id}`)
                    }
                  >
                    <HiOutlineEye />

                    <span>Open</span>
                  </button>

                  {can("organization.admin") && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/dashboard/organizations/${organization._id}/edit`,
                        )
                      }
                    >
                      <HiOutlinePencilSquare />

                      <span>Wijzig</span>
                    </button>
                  )}

                  {can("organization.admin") && (
                    <button
                      type="button"
                      disabled={isDeleting}
                      className="organizations-page__danger-action"
                      onClick={() =>
                        setConfirmModal({
                          isOpen: true,
                          organization,
                        })
                      }
                    >
                      <HiOutlineTrash />

                      <span>Verwijder</span>
                    </button>
                  )}
                </div>
              </PageCard>
            ))}
          </div>

          {/* PAGINATION */}
          {pagination.pages > 1 && (
            <div className="organizations-page__pagination">
              <button
                disabled={pagination.page <= 1}
                onClick={() =>
                  dispatch(setOrganizationPage(pagination.page - 1))
                }
              >
                Vorige
              </button>

              <span>
                Pagina {pagination.page} van {pagination.pages}
              </span>

              <button
                disabled={pagination.page >= pagination.pages}
                onClick={() =>
                  dispatch(setOrganizationPage(pagination.page + 1))
                }
              >
                Volgende
              </button>
            </div>
          )}
        </>
      )}

      {/* DELETE MODAL */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Organisatie verwijderen?"
        message="Deze organisatie wordt permanent verwijderd."
        confirmText="Verwijderen"
        variant="danger"
        onClose={() =>
          setConfirmModal({
            isOpen: false,
            organization: null,
          })
        }
        onConfirm={handleDelete}
      />
    </div>
  );
}
